// src/ui/screens/post/AddStatusScreen.jsx

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { db, auth, storage } from '../../../firebase';
import notificationService from '../../../services/notificationService';

const kOrange = '#FF8C08';
const kBg = '#08090B';
const kCard = '#23262C';

// WhatsApp statuses live for 24 hours — matched here.
const STATUS_LIFETIME_MS = 24 * 60 * 60 * 1000;

// Viewport width at which the desktop layout kicks in.
const DESKTOP_BREAKPOINT = 900;

// Same values as Flutter's Color(0xFF......).value
const TEXT_STATUS_COLORS = [
  0xff075e54,
  0xff128c7e,
  0xff25d366,
  0xff34495e,
  0xff8e44ad,
  0xffc0392b,
  0xff2980b9,
  0xffd35400,
  0xff16a085,
  0xff23262c,
];

const STATUS_MODES = Object.freeze({
  choose: 'choose',
  text: 'text',
  photo: 'photo',
  camera: 'camera',
});

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Tracks whether the viewport is at desktop width.
 */
function useIsDesktop(breakpoint = DESKTOP_BREAKPOINT) {
  const query = `(min-width: ${breakpoint}px)`;
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isDesktop;
}

/**
 * True when we should open our own live camera preview (desktop browsers).
 * Touch devices keep using the native camera via <input capture>, which
 * browsers ignore on desktop.
 */
function canUseLiveCamera() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return false;
  }
  const isTouchPrimary =
    window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  return !isTouchPrimary;
}

/**
 * Mirrors Flutter's ImagePicker(maxWidth, maxHeight, imageQuality):
 * downscale to fit inside the box and re-encode as JPEG. Falls back to the
 * original file if anything goes wrong.
 */
async function compressImage(file, maxW, maxH, quality) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxW / bitmap.width, maxH / bitmap.height);
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    return blob || file;
  } catch (_) {
    return file;
  }
}

/**
 * Add Status screen. Mirrors the Flutter AddStatusScreen:
 *  - Choose between text / gallery photo / camera photo
 *  - Text editor with color palette
 *  - Photo editor with caption + send button
 *  - Posts to Firestore `status/{id}` with 24h expiry
 *  - Fire-and-forget subscriber notifications
 */
export default function AddStatusScreen() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const [mode, setMode] = useState(STATUS_MODES.choose);
  const [text, setText] = useState('');
  const [colorIndex, setColorIndex] = useState(0);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [snack, setSnack] = useState(null);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const snackTimer = useRef(null);

  // ── Helpers ──────────────────────────────────────────────────────────

  const showSnack = (msg, isError = false) => {
    setSnack({ msg, isError });
    if (snackTimer.current) clearTimeout(snackTimer.current);
    snackTimer.current = setTimeout(() => setSnack(null), 3000);
  };

  useEffect(() => {
    return () => {
      if (snackTimer.current) clearTimeout(snackTimer.current);
    };
  }, []);

  // Revoke the preview URL whenever it changes / on unmount.
  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  // Same as Flutter's Navigator.pop — with a fallback for direct links.
  const leave = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  // ── Mode entry ───────────────────────────────────────────────────────

  const enterTextMode = () => setMode(STATUS_MODES.text);

  const handlePhotoPicked = (file) => {
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setMode(STATUS_MODES.photo);
  };

  // Desktop: live camera preview. Touch devices: native camera via input.
  const openCamera = () => {
    if (canUseLiveCamera()) {
      setMode(STATUS_MODES.camera);
    } else {
      cameraInputRef.current?.click();
    }
  };

  const closeCamera = () => setMode(STATUS_MODES.choose);

  const handleCameraError = (err) => {
    console.warn('Camera error:', err);
    setMode(STATUS_MODES.choose);
    showSnack('Could not access the camera. Check permissions.', true);
  };

  const backToChoose = () => {
    setMode(STATUS_MODES.choose);
    setPhotoFile(null);
    setPhotoPreviewUrl(null);
    setText('');
    setCaption('');
    setColorIndex(0);
  };

  // ── Subscriber notification (best-effort) ────────────────────────────
  //
  // onStatusPosted fans out to everyone subscribed to this user and expects
  // a plain uid list, so read the poster's own `subscribers` array first.

  const notifySubscribers = async ({ uid, statusId, thumbnailUrl = null }) => {
    try {
      const userSnap = await getDoc(doc(db, 'users', uid));
      const subscriberUids = userSnap.data()?.subscribers ?? [];
      if (!Array.isArray(subscriberUids) || subscriberUids.length === 0) return;
      await notificationService.onStatusPosted({
        subscriberUids,
        statusId,
        thumbnailUrl,
      });
    } catch (e) {
      console.warn('Status subscriber notify error:', e);
    }
  };

  // ── Post: text ───────────────────────────────────────────────────────

  const postTextStatus = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      showSnack('Write something first', true);
      return;
    }

    setIsUploading(true);
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Not signed in');

      const statusId = newId();
      const expiresAt = new Date(Date.now() + STATUS_LIFETIME_MS);

      // Status posting requires no prior verification, unlike listings.
      await setDoc(doc(db, 'status', statusId), {
        id: statusId,
        uid,
        type: 'text',
        text: trimmed,
        backgroundColor: TEXT_STATUS_COLORS[colorIndex],
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
      });

      // Fire-and-forget — never block the navigation on this.
      notifySubscribers({ uid, statusId });

      leave();
    } catch (e) {
      console.error('Text status error:', e);
      setIsUploading(false);
      showSnack('Could not post status. Try again.', true);
    }
  };

  // ── Post: photo ──────────────────────────────────────────────────────

  const postPhotoStatus = async () => {
    if (!photoFile) return;

    setIsUploading(true);
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Not signed in');

      const statusId = newId();
      const expiresAt = new Date(Date.now() + STATUS_LIFETIME_MS);

      // Flutter: maxWidth 1920, maxHeight 1920, imageQuality 88
      const blob = await compressImage(photoFile, 1920, 1920, 0.88);

      const fileRef = storageRef(storage, `status/${uid}/${statusId}.jpg`);
      await uploadBytes(fileRef, blob, { contentType: 'image/jpeg' });
      const imageUrl = await getDownloadURL(fileRef);

      await setDoc(doc(db, 'status', statusId), {
        id: statusId,
        uid,
        type: 'image',
        imageUrl,
        caption: caption.trim(),
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
      });

      // Fire-and-forget — never block the navigation on this.
      notifySubscribers({ uid, statusId, thumbnailUrl: imageUrl });

      leave();
    } catch (e) {
      console.error('Photo status error:', e);
      setIsUploading(false);
      showSnack('Could not post status. Try again.', true);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <>
      {mode === STATUS_MODES.choose && (
        <ChooseScreen
          isDesktop={isDesktop}
          onBack={leave}
          onTextMode={enterTextMode}
          onGallery={() => galleryInputRef.current?.click()}
          onCamera={openCamera}
        />
      )}

      {mode === STATUS_MODES.text && (
        <TextEditor
          isDesktop={isDesktop}
          text={text}
          setText={setText}
          colorIndex={colorIndex}
          setColorIndex={setColorIndex}
          isUploading={isUploading}
          onBack={backToChoose}
          onPost={postTextStatus}
        />
      )}

      {mode === STATUS_MODES.camera && (
        <CameraCapture
          isDesktop={isDesktop}
          onClose={closeCamera}
          onCaptured={handlePhotoPicked}
          onError={handleCameraError}
        />
      )}

      {mode === STATUS_MODES.photo && (
        <PhotoEditor
          isDesktop={isDesktop}
          previewUrl={photoPreviewUrl}
          caption={caption}
          setCaption={setCaption}
          isUploading={isUploading}
          onBack={backToChoose}
          onPost={postPhotoStatus}
        />
      )}

      {/* Hidden file inputs */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          handlePhotoPicked(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          handlePhotoPicked(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {snack && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: snack.isError ? '#ff5252' : kOrange,
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            zIndex: 1000,
            maxWidth: '90%',
          }}
        >
          {snack.msg}
        </div>
      )}
    </>
  );
}

// ── Choose screen ────────────────────────────────────────────────────────

function ChooseScreen({ isDesktop, onBack, onTextMode, onGallery, onCamera }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: kBg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isDesktop ? 'center' : 'stretch',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: isDesktop ? 560 : 'none',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: isDesktop ? '32px 24px 4px' : '8px 16px 4px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <BackButton onClick={onBack} />
          <h1
            style={{
              color: '#fff',
              fontSize: isDesktop ? 24 : 20,
              fontWeight: 700,
              margin: 0,
            }}
          >
            Add Status
          </h1>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: isDesktop ? '0 24px 96px' : '0 24px 48px',
            gap: 16,
          }}
        >
          <ChooseOptionCard
            icon={<TextIcon />}
            title="Text Status"
            subtitle="Share a thought with a colored background"
            onClick={onTextMode}
          />
          <ChooseOptionCard
            icon={<PhotoIcon />}
            title="Photo from Gallery"
            subtitle="Pick a photo and add a caption"
            onClick={onGallery}
          />
          <ChooseOptionCard
            icon={<CameraIcon />}
            title="Take a Photo"
            subtitle="Capture a moment right now"
            onClick={onCamera}
          />
        </div>
      </div>
    </div>
  );
}

// ── Camera capture (desktop live preview) ────────────────────────────────

function CameraCapture({ isDesktop, onClose, onCaptured, onError }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  // Keep the latest callbacks without restarting the stream.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          try {
            await video.play();
          } catch (_) {
            // Autoplay can reject if the element unmounts mid-start.
          }
        }
      } catch (e) {
        if (!cancelled) onErrorRef.current?.(e);
      }
    };

    start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, []);

  const capture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.92)
    );
    if (!blob) return;

    const file = new File([blob], `camera-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });

    stopStream();
    onCaptured(file);
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        height: '100dvh',
        background: '#000',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: isDesktop ? '24px 24px 8px' : 0,
          position: 'relative',
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          onLoadedMetadata={() => setIsReady(true)}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: isDesktop ? 12 : 0,
            // Mirror the preview like a selfie view; the saved photo is not mirrored.
            transform: 'scaleX(-1)',
            background: '#000',
          }}
        />

        {!isReady && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner color={kOrange} size={32} />
          </div>
        )}
      </div>

      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          left: isDesktop ? 24 : 8,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.45)',
          border: 'none',
          color: '#fff',
          fontSize: 18,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>

      <div
        style={{
          padding: '12px 16px calc(env(safe-area-inset-bottom, 0px) + 24px)',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={capture}
          disabled={!isReady}
          aria-label="Take photo"
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'transparent',
            border: '4px solid #fff',
            padding: 4,
            cursor: isReady ? 'pointer' : 'default',
            opacity: isReady ? 1 : 0.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: kOrange,
              display: 'block',
            }}
          />
        </button>
      </div>
    </div>
  );
}

// ── Text editor ──────────────────────────────────────────────────────────

function TextEditor({
  isDesktop,
  text,
  setText,
  colorIndex,
  setColorIndex,
  isUploading,
  onBack,
  onPost,
}) {
  const bgColor = hexFromInt(TEXT_STATUS_COLORS[colorIndex]);

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: bgColor,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <button
        onClick={isUploading ? undefined : onBack}
        disabled={isUploading}
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          left: isDesktop ? 24 : 8,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.25)',
          border: 'none',
          color: '#fff',
          fontSize: 18,
          cursor: isUploading ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 32px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What's on your mind?"
          style={{
            width: '100%',
            maxWidth: isDesktop ? 760 : 'none',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            resize: 'none',
            color: '#fff',
            fontSize: isDesktop ? 34 : 26,
            fontWeight: 600,
            textAlign: 'center',
            caretColor: '#fff',
            minHeight: isDesktop ? 180 : 120,
            fontFamily: 'inherit',
          }}
        />
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: isDesktop ? 520 : 'none',
          boxSizing: 'border-box',
          padding: '0 0 calc(env(safe-area-inset-bottom, 0px) + 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isDesktop ? 'center' : 'flex-start',
            gap: 10,
            overflowX: 'auto',
            padding: '4px 20px',
            height: 44,
          }}
        >
          {TEXT_STATUS_COLORS.map((c, i) => {
            const isSelected = i === colorIndex;
            return (
              <button
                key={i}
                onClick={() => setColorIndex(i)}
                style={{
                  flexShrink: 0,
                  width: isSelected ? 40 : 32,
                  height: isSelected ? 40 : 32,
                  borderRadius: '50%',
                  background: hexFromInt(c),
                  border: `${isSelected ? 2.5 : 1}px solid #fff`,
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.15s',
                }}
              />
            );
          })}
        </div>

        <div style={{ padding: '0 20px' }}>
          {/* Like Flutter: only disabled while uploading — an empty post
              shows the "Write something first" snack instead. */}
          <button
            onClick={onPost}
            disabled={isUploading}
            style={{
              width: '100%',
              height: 50,
              background: '#fff',
              color: bgColor,
              fontSize: 15,
              fontWeight: 700,
              border: 'none',
              borderRadius: 14,
              cursor: isUploading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isUploading ? (
              <Spinner color={kOrange} size={20} />
            ) : (
              'Post Status'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Photo editor ─────────────────────────────────────────────────────────

function PhotoEditor({
  isDesktop,
  previewUrl,
  caption,
  setCaption,
  isUploading,
  onBack,
  onPost,
}) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        height: '100dvh',
        background: '#000',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: isDesktop ? '24px 24px 8px' : 0,
        }}
      >
        <img
          src={previewUrl}
          alt=""
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: isDesktop ? 12 : 0,
          }}
        />
      </div>

      <button
        onClick={isUploading ? undefined : onBack}
        disabled={isUploading}
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          left: isDesktop ? 24 : 8,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.45)',
          border: 'none',
          color: '#fff',
          fontSize: 18,
          cursor: isUploading ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>

      <div
        style={{
          padding: '0 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: isDesktop ? 720 : 'none',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.24)',
            borderRadius: 16,
            padding: '6px 14px',
          }}
        >
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Add a caption…"
            rows={1}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              color: '#fff',
              fontSize: 14,
              caretColor: kOrange,
              minHeight: 24,
              maxHeight: 72,
              padding: '8px 0',
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={onPost}
            disabled={isUploading}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: kOrange,
              border: 'none',
              color: '#000',
              cursor: isUploading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isUploading ? <Spinner color="#000" size={20} /> : <SendIcon />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────

function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 40,
        height: 40,
        borderRadius: 20,
        background: kCard,
        border: '1px solid rgba(255,255,255,0.12)',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      ←
    </button>
  );
}

function ChooseOptionCard({ icon, title, subtitle, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: 18,
        background: kCard,
        borderRadius: 18,
        border: '1px solid rgba(255,255,255,0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: 14,
          background: 'rgba(255,140,8,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: kOrange,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>
          {title}
        </div>
        <div
          style={{
            color: 'rgba(255,255,255,0.38)',
            fontSize: 12,
            marginTop: 3,
          }}
        >
          {subtitle}
        </div>
      </div>
      <span style={{ color: kOrange, fontSize: 20 }}>›</span>
    </div>
  );
}

function Spinner({ color, size = 20 }) {
  return (
    <>
      <div
        style={{
          width: size,
          height: size,
          border: `2px solid ${color}`,
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// Convert 0xAARRGGBB int to #RRGGBB hex
function hexFromInt(n) {
  return '#' + (n & 0xffffff).toString(16).padStart(6, '0');
}

function TextIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 6h16M4 12h10M4 18h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PhotoIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="9" cy="10" r="2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M3 17l5-5 5 5 3-3 5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 8h3l2-3h6l2 3h3v11H4V8z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 20l18-8L3 4v6l12 2-12 2v6z" fill="currentColor" />
    </svg>
  );
}